import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, InputGroup } from 'react-bootstrap';
import { useMutation } from '@apollo/client';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { LOGIN } from '../graphql/operations';
import Footer from '../components/Footer';

interface LoginPageProps {
  onLoginSuccess: () => void;
  isAdminLogin?: boolean;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess, isAdminLogin = false }) => {
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const isAdminFlow = isAdminLogin;

  useEffect(() => {
    if (location.state && (location.state as any).from === '/order') {
      setInfoMessage('Please log in to complete your checkout.');
    }
  }, [location]);

  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (token) {
      if (role === 'admin' || role === 'manager' || role === 'admin-manager') {
        navigate('/admin');
      } else {
        const redirectPath = (location.state as any)?.from || '/';
        const redirectState = (location.state as any)?.fromState || null;
        navigate(redirectPath, { state: redirectState });
      }
    }
  }, [navigate, location.state]);

  const [loginMutation, { loading }] = useMutation(LOGIN, {
    onError: (err) => {
      setErrorMessage(err.message || 'Invalid username or password.');
    },
    onCompleted: (data) => {
      if (data?.login?.token) {
        localStorage.setItem('token', data.login.token);
        localStorage.setItem('username', data.login.user.username);
        localStorage.setItem('role', data.login.user.role);
        localStorage.setItem('companyName', data.login.user.companyName || '');
        localStorage.setItem('gstNumber', data.login.user.gstNumber || '');
        localStorage.setItem('companyAddress', data.login.user.companyAddress || '');
        localStorage.setItem('shippingAddress', data.login.user.shippingAddress || '');
        onLoginSuccess();
        if (isAdminFlow) {
          if (data.login.user.role === 'admin' || data.login.user.role === 'manager' || data.login.user.role === 'admin-manager') {
            navigate('/admin');
          } else {
            setErrorMessage('This login is for authorized staff only.');
          }
        } else if (data.login.user.role === 'admin' || data.login.user.role === 'manager' || data.login.user.role === 'admin-manager') {
          navigate('/admin');
        } else {
          const redirectPath = (location.state as any)?.from || '/';
          const redirectState = (location.state as any)?.fromState || null;
          navigate(redirectPath, { state: redirectState });
        }
      } else {
        setErrorMessage('Failed to receive authorization token.');
      }
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (!username.trim() || !password.trim()) {
      setErrorMessage('Please enter both username and password.');
      return;
    }

    try {
      await loginMutation({
        variables: {
          username: username.trim(),
          password: password,
        },
      });
    } catch (err) {
      // Handled by onError callback
    }
  };

  return (
    <div className="page-fade d-flex flex-column login-page-wrapper" style={{ minHeight: 'calc(100vh - 68px)', backgroundColor: 'var(--bg-secondary)' }}>
      <div className="d-flex align-items-center justify-content-center flex-grow-1 py-5" style={{ background: 'linear-gradient(135deg, rgba(233, 30, 140, 0.03) 0%, rgba(59, 91, 219, 0.03) 100%)' }}>
        <Container style={{ maxWidth: '440px' }}>
          <Card className="border-0 shadow-lg p-3 bg-white rounded-4" style={{ overflow: 'hidden', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.8)' }}>
            <div className="text-center pt-4 pb-3">
              <div className="d-inline-flex align-items-center justify-content-center bg-light rounded-3 p-3 mb-3 border" style={{ width: '60px', height: '60px', color: isAdminFlow ? '#3b5bdb' : 'var(--pink)' }}>
                <i className={isAdminFlow ? 'fas fa-user-shield' : 'fas fa-user'} style={{ fontSize: '24px' }}></i>
              </div>
              <h3 className="fw-bold text-dark mb-1">
                {isAdminFlow ? 'Admin Portal' : 'Welcome Back'}
              </h3>
              <p className="text-muted" style={{ fontSize: '13px' }}>
                {isAdminFlow ? 'Prathitha Digital Printers Admin Login' : 'Sign in to manage your printing orders'}
              </p>
            </div>

            <Card.Body className="px-4 pb-4 pt-2">
              {errorMessage && (
                <Alert variant="danger" className="border-0 py-2 rounded-3 mb-4 d-flex align-items-center gap-2" style={{ fontSize: '12px' }}>
                  <i className="fas fa-exclamation-triangle"></i>
                  <div>{errorMessage}</div>
                </Alert>
              )}

              {infoMessage && (
                <Alert variant="info" className="border-0 py-2.5 rounded-3 mb-4 d-flex align-items-center gap-2" style={{ fontSize: '12.5px', backgroundColor: '#eef2ff', color: '#3b5bdb' }}>
                  <i className="fas fa-info-circle"></i>
                  <div>{infoMessage}</div>
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="formUsername">
                  <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '12px' }}>Username</Form.Label>
                  <InputGroup className="border rounded-3 overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
                    <InputGroup.Text className="bg-white border-0 text-muted px-3">
                      <i className="far fa-user"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      className="border-0 py-2 form-input-login"
                      placeholder="Enter username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      style={{ fontSize: '14px', outline: 'none', boxShadow: 'none' }}
                      autoComplete="username"
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-4" controlId="formPassword">
                  <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '12px' }}>Password</Form.Label>
                  <InputGroup className="border rounded-3 overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
                    <InputGroup.Text className="bg-white border-0 text-muted px-3">
                      <i className="fas fa-key"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      className="border-0 py-2 form-input-login"
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{ fontSize: '14px', outline: 'none', boxShadow: 'none' }}
                      autoComplete="current-password"
                    />
                    <Button 
                      variant="link" 
                      className="bg-white border-0 text-muted px-3"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ boxShadow: 'none' }}
                    >
                      <i className={showPassword ? 'far fa-eye-slash' : 'far fa-eye'}></i>
                    </Button>
                  </InputGroup>
                </Form.Group>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-100 py-2 fw-semibold text-white d-flex align-items-center justify-content-center gap-2"
                  style={{
                    background: 'linear-gradient(90deg, #e91e8c 0%, #3b5bdb 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '14px',
                    transition: 'all 0.2s',
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
                  onMouseOut={(e) => (e.currentTarget.style.transform = 'none')}
                >
                  {loading ? (
                    <><i className="fas fa-spinner fa-spin me-1"></i> Logging in...</>
                  ) : (
                    <>Sign In <i className="fas fa-arrow-right" style={{ fontSize: '11px' }}></i></>
                  )}
                </Button>

                {isAdminFlow ? (
                  <div className="text-center mt-3 text-muted small">
                    Authorized Administrator access only.
                  </div>
                ) : (
                  <div className="text-center mt-3">
                    <span className="text-muted small">Don't have an account? </span>
                    <Link to="/register" state={location.state} className="small fw-semibold text-decoration-none" style={{ color: 'var(--pink)' }}>Sign Up</Link>
                  </div>
                )}
              </Form>
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />
    </div>
  );
};

export default LoginPage;
